import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
  MonetizationOn as MonetizationOnIcon,
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout, MenuItem } from 'src/components/common';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useCurrentUser, useUserAvatar } from 'src/hooks/user';
import { employerReceivesOwnRevenueShare } from 'src/utils/revenueShare';
import { getUserFullName } from 'src/utils/user';

export const AccountLayoutEmployerAdmin: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { id: userId, user } = useCurrentUser();
  const { userAvatar, userAvatarLoading, updateUserAvatar, userAvatarUpdating } =
    useUserAvatar(userId);

  const { employer, loading } = useCurrentUserEmployer();

  const fullName = getUserFullName(user);
  const title = user?.title;

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: appRoutes.account.personalInformation,
        title: 'Personal Information',
        icon: PersonIcon,
      },
      {
        link: appRoutes.account.companyInformation,
        title: 'Company information',
        icon: AccountBalanceIcon,
      },
      {
        link: appRoutes.account.employerDisclaimer,
        title: 'Employer Disclaimer',
        icon: InfoIcon,
      },
      {
        link: appRoutes.account.wallet,
        title: 'Wallet',
        icon: MonetizationOnIcon,
        hide: !employer?.revenueShareSetting || !employerReceivesOwnRevenueShare(employer),
      },
      {
        link: appRoutes.account.settings,
        title: 'Settings',
        icon: SettingsIcon,
      },
      {
        link: appRoutes.account.notifications,
        title: 'Notifications',
        icon: NotificationsIcon,
        divider: true,
      },
      {
        link: appRoutes.account.security,
        title: 'Sign-in & Security',
        icon: PasswordIcon,
      },
      {
        link: appRoutes.account.setupMfa,
        title: 'Multi-factor Authentication',
        icon: PhonelinkIcon,
      },
    ],
    [employer],
  );

  return (
    <ItemDetailsLayout
      name={fullName}
      subtitle={title}
      logoUrl={userAvatar?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updateUserAvatar,
        loading: userAvatarLoading || userAvatarUpdating,
      }}
      loading={loading}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};
